import { computed, isReactive, isRef, reactive, ref, shallowRef, toValue, watch } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { v4 as uuid4 } from 'uuid'
import { result, isString, set } from 'lodash-es'
import datepicker from '@provise/components/datepicker.vue'
import SelectIdObject from '@/components/select/selectIdObject.vue'
import Editor from '@/components/editor/editor.vue'
import f from './fields'
import VueDatePicker from '@vuepic/vue-datepicker'

const fl = {
  text: [f.xinput, { type: 'text' }],
  number: [f.xinput, { type: 'number' }],
  textarea: [f.xtextarea],
  date: [f.xinput, { type: 'date' }],
  datetime: [f.xinput, { type: 'datetime-local' }],
  datepicker: [datepicker],
  dtpicker: [
    VueDatePicker,
    {
      'auto-apply': false,
      'text-input': true,
      config: {
        setDateOnMenuClose: true,
      },
      enableTimePicker: false,
      autoApply: true,
      locale: 'ru-RU',
      format: 'dd.MM.yyyy',
      'select-text': 'Выбрать',
      'cancel-text': 'Отмена',
    },
  ],
  checkbox: [f.xcheckbox],
  radios: [f.xradios],
  editor: [Editor],
  link: [f.xlink, { layout: false }],
  button: [f.xbutton, { layout: false }],
  select: [f.xselect],
  'select-id': [SelectIdObject],
  'select-medicine': [f.xmedicine],
  'select-archive': [f.xarcfolder],
  'select-department': [f.xdepartment],
}

const vlidationMessages = {
  format: 'Недопустимый формат данных',
}
const validatorFloatRe = /^\d+((\.|,)\d+)?$/
export const validators = {
  float: v => (validatorFloatRe.test(v) ? undefined : vlidationMessages.format),
}

export const useSimpleSchema = (schema, model, keyPrefix) => {
  /*
   * Fields
   */
  const ca = new Map() // refs
  const cs = new Map() // uuids

  let sref = shallowRef([])
  const sdeps = new Map()
  const sshow = ref({})

  fsref(toValue(schema))

  if (isRef(schema)) {
    watch(schema, fsref)
  }

  watch(
    model,
    m => {
      const ss = sshow.value
      for (const [x, child] of sdeps) {
        for (const [cname, cshow] of Object.entries(child)) {
          let v, res
          if (x.k instanceof Function) {
            res = x.k(m)
            v = true
          } else {
            v = toValue(x.v)
            res = result(m, x.k)
          }

          // show-if
          if (cshow) {
            if (Array.isArray(v)) {
              ss[cname] = v.some(v => res === toValue(v))
            } else {
              ss[cname] = res === v
            }
            // show-if-not
          } else {
            if (Array.isArray(v)) {
              ss[cname] = v.every(v => res !== toValue(v))
            } else {
              ss[cname] = res !== v
            }
          }
        }
      }
      sshow.value = ss
      fsref(toValue(schema))
    },
    { immediate: true }
  )

  function fsref(sch = []) {
    if (isString(sch)) {
      sch = JSON.parse(sch)
    }
    schemaDeps(sch, sdeps)
    sref.value = sch.map(s => {
      if (!Array.isArray(s)) {
        return []
      }
      const [type, fieldName, label, props, ...children] = s
      let ctrl
      let pro = {}
      let uuid = uuid4()

      if (fl[type]) {
        ;[ctrl, pro] = fl[type]
      } else {
        if (type && type.render instanceof Function) {
          ctrl = type
        } else {
          ctrl = f.xdiv
          pro.is = type
        }
      }

      let validator = props?.validate
      if (validator) {
        if (validator == 'float') {
          props.validate = validators.float
        }
      }

      if (fieldName) {
        if (cs.has(fieldName)) {
          uuid = cs.get(fieldName)
        } else {
          cs.set(fieldName, uuid)
        }
      } else {
        uuid = uuid4()
      }

      const p = { ...props, ...pro }
      let _getm, _setm
      if (p['model-value']) {
        _getm = p['model-value']
      } else {
        _getm = getm.call(ca, model, fieldName)
      }
      if (p['onUpdate:modelValue']) {
        _setm = p['onUpdate:modelValue']
      } else {
        _setm = setm.call(ca, model, fieldName)
      }

      const prefix = toValue(keyPrefix)
      if (prefix && uuid) {
        uuid = `${prefix}:${uuid}`
      }

      return [
        // component
        ctrl,
        // key/id
        uuid,
        // props
        reactive({ ...props, ...pro }),
        // :model-value
        _getm,
        // @update:model-value
        _setm,
        label,
        children,
        fieldName,
      ]
    })
  }

  return { sref, sshow }
}

export const binder = $props => reactiveOmit($props, 'modelValue')

const mdlr = (m, n) =>
  computed({
    get() {
      if (isReactive(m)) {
        return result(m, n)
      }

      if (isRef(m)) {
        return result(m.value || {}, n)
      }
      return null
    },
    set(v) {
      if (n === null || n === undefined || n === false) {
        return
      }
      if (isReactive(m)) {
        const mv = { ...m }
        set(mv, n, v)
        Object.assign(m, mv)
        return
      }
      if (isRef(m)) {
        const mv = { ...m.value }
        set(mv, n, v)
        m.value = mv
        return
      }
    },
  })

function getm(m, n) {
  if (this.has(n)) {
    return this.get(n)
  }
  const r = mdlr(m, n)
  this.set(n, r)
  return r
}
function setm(m, n) {
  if (!this.has(n)) {
    const r = mdlr(m, n)
    this.set(n, r)
  }
  return v => {
    this.get(n).value = v
  }
}

function schemaDeps(arr, r) {
  const rkeys = []
  for (let s of arr) {
    const fname = result(s, '[1]')
    if (!fname) {
      // cant control visibility if no field name
      continue
    }

    const showif = result(s, '[3].show-if')
    const showifnot = result(s, '[3].show-if-not')
    const chld = s.slice(4)
    if (chld && chld.length) {
      schemaDeps(chld, r)
    }

    if (!(Array.isArray(showif) || Array.isArray(showifnot))) {
      continue
    }

    if (showif) {
      fillr(showif, true)
    }
    if (showifnot) {
      fillr(showifnot, false)
    }

    function fillr(a, b) {
      const [k, v] = a
      let kob = { k, v }
      let chlds = null

      const io = rkeys.findIndex(({ k, v }) => k == a[0] && v == a[1])

      if (io == -1) {
        rkeys.push(kob)
      } else {
        kob = rkeys[io]
      }

      for (const [rk, rv] of r) {
        if (rk.k == k && rk.v == v) {
          chlds = rv
        }
      }
      if (chlds == null) {
        chlds = {}
      }
      chlds[fname] = b
      r.set(kob, chlds)
    }
  }
  return r
}
